import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import Preloader from '../components/Preloader';
import Footer from '../components/Footer';
import { FaArrowLeft } from 'react-icons/fa';
import ScrollReveal from '../components/ScrollReveal';

const safarisData = [
    {
        id: 1,
        title: 'Shai Hills Resource Reserve',
        region: 'Greater Accra',
        description: 'A wildlife reserve featuring baboons, zebras, and scenic rocky hills near Accra.',
        image: '/assets/shaihills.jpg',
        fullContent: 'Shai Hills Resource Reserve is the closest wildlife reserve to Accra. It combines nature conservation, rich cultural history, and archaeological sites. The reserve is home to troops of olive baboons, kobs, zebras, and ostriches. Visitors can explore the ancestral caves of the Shai people and enjoy rock climbing.',
        wildlife: ['Olive Baboons', 'Zebras', 'Ostriches', 'Kobs', 'Monitor Lizards'],
        bestTime: 'All Year Round',
        images: ['/assets/zebras-at-shai-hills-ghana.jpg', '/assets/shailhills2.jpg', '/assets/shaihills3.jpg'],
        activities: ['Game Viewing', 'Rock Climbing', 'Cave Exploration', 'Bird Watching'],
    },
    {
        id: 2,
        title: 'Aquila Private Game Reserve',
        region: 'Western Cape',
        description: 'A premier Big 5 safari destination offering luxury and wildlife encounters near Cape Town.',
        image: '/assets/accommodation-aquilasafari.avif',
        fullContent: 'Aquila Private Game Reserve offers a Big 5 safari experience just two hours from Cape Town. Set in a private reserve of 10,000 hectares, it is home to lions, leopards, rhinos, elephants, and buffalo. The reserve is dedicated to conservation and offers game drives, horseback safaris, and quad bike safaris.',
        wildlife: ['Lions', 'Leopards', 'Elephants', 'Rhinos', 'Buffalo'],
        bestTime: 'All Year Round',
        images: ['/assets/aquila-private-game-reserve.jpg', '/assets/aquilasafari.jpg', '/assets/aquilasafari2.jpg'],
        activities: ['Game Drives', 'Horseback Safaris', 'Quad Biking', 'Spa Treatments'],
    },
    {
        id: 3,
        title: 'Addis Ababa Zoo and Botanical Garden',
        region: 'Addis Ababa',
        description: 'Urban wildlife sanctuary featuring Ethiopian endemic species and endangered animals.',
        image: '/assets/Addis_Abeba_Lion_Zoo_gate,_Ethiopia,_2004-11-07.jpg',
        fullContent: 'The Addis Ababa Zoo and Botanical Garden is Africa\'s oldest zoo, established in 1948. The facility houses numerous Ethiopian endemic species and endangered animals in well-designed habitats. Visitors can observe lions, leopards, primates, and various bird species while exploring the beautiful botanical gardens featuring native Ethiopian plants and trees.',
        wildlife: ['Ethiopian Lion', 'Leopards', 'Primates', 'Antelopes', 'Endemic Birds'],
        bestTime: 'October to May',
        images: ['/assets/addis-ababa-gullele-botanical-garden.webp', '/assets/zoma-museum-garden-view.jpg', '/assets/JNC-Gullele-03.jpg'],
        activities: ['Zoo Tour', 'Botanical Walk', 'Educational Tours', 'Photography'],
    },
];

const SafariDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const safari = safarisData.find(s => s.id === parseInt(id));

    if (!safari) {
        return (
            <div className='font-nixie-one bg-gray-50 min-h-screen text-gray-800'>
                <PageHeader />
                <Preloader />
                <main className="flex items-center justify-center min-h-screen">
                    <div className="text-center">
                        <h2 className="text-4xl font-bold mb-4">Safari Not Found</h2>
                        <button onClick={() => navigate('/wildlife-safaris')} className="px-6 py-2 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-colors duration-300">
                            Back to Wildlife Safaris
                        </button>
                    </div>
                </main>
                <Footer />
            </div>
        );
    }

    return (
        <div className='font-nixie-one bg-gray-50 min-h-screen text-gray-800'>
            <PageHeader />
            <Preloader />
            <main>
                {/* Back Button */}
                <section className="bg-white py-6 px-4 border-b">
                    <div className="container mx-auto">
                        <button onClick={() => navigate('/wildlife-safaris')} className="flex items-center gap-2 text-green-600 hover:text-green-700 font-semibold transition-colors duration-300">
                            <FaArrowLeft /> Back to Wildlife Safaris
                        </button>
                    </div>
                </section>

                {/* Hero Section */}
                <section className="relative h-96 bg-cover bg-center">
                    <img src={safari.image} alt={safari.title} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/40"></div>
                    <div className="absolute inset-0 flex items-end justify-start">
                        <div className="p-8 text-white">
                            <span className="inline-block bg-green-600 text-white text-xs font-bold px-3 py-1 rounded mb-4">{safari.region}</span>
                            <h1 className="text-4xl md:text-5xl font-cormorant-upright font-bold">{safari.title}</h1>
                        </div>
                    </div>
                </section>

                {/* Content Section */}
                <section className="py-16 px-4">
                    <div className="container mx-auto max-w-4xl">
                        <ScrollReveal animationClass="animate-fade-in" threshold={0.1}>
                            <article className="bg-white rounded-lg shadow-lg p-8">
                                <h2 className="text-2xl font-cormorant-upright font-bold mb-6">About This Safari</h2>
                                <p className="text-gray-700 font-semibold leading-relaxed mb-8 text-sm">{safari.fullContent}</p>

                                {/* Photo Gallery */}
                                <div className="mb-8">
                                    <h3 className="text-2xl font-cormorant-upright font-bold mb-6">Safari Gallery</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                        {safari.images.map((image, index) => (
                                            <div key={index} className="rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow duration-300">
                                                <img
                                                    src={image}
                                                    alt={`${safari.title} ${index + 1}`}
                                                    className="w-full h-48 object-cover transform hover:scale-105 transition-transform duration-500"
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Wildlife */}
                                <div className="border-t pt-8 mb-8">
                                    <h3 className="text-2xl font-cormorant-upright font-bold mb-6">Wildlife You May See</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {safari.wildlife.map((animal, index) => (
                                            <div key={index} className="bg-green-50 border-l-4 border-green-600 p-4 rounded">
                                                <p className="text-gray-700 font-semibold text-sm">{animal}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Activities */}
                                <div className="border-b pb-8 mb-8">
                                    <h3 className="text-2xl font-cormorant-upright font-bold mb-6">Activities</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {safari.activities.map((activity, index) => (
                                            <div key={index} className="bg-blue-50 border-l-4 border-blue-600 p-4 rounded">
                                                <p className="text-gray-700 font-semibold text-sm">{activity}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Best Time to Visit */}
                                <div className="bg-orange-50 border-l-4 border-orange-600 p-6 rounded mb-8">
                                    <h4 className="font-bold text-base text-orange-600 mb-2">Best Time to Visit</h4>
                                    <p className="text-gray-700 text-sm">{safari.bestTime}</p>
                                </div>

                                {/* Call to Action */}
                                <div className="bg-gradient-to-r from-green-600 to-green-700 text-white p-6 rounded">
                                    <h4 className="font-bold text-base mb-2">Plan Your Safari Adventure</h4>
                                    <p className="mb-4 text-sm">Embark on an unforgettable wildlife safari and experience the wonders of Ghana\'s natural biodiversity.</p>
                                    <a href='/contact-us'>
                                    <button className="px-6 py-2 capitalize text-sm bg-white text-green-600 font-semibold rounded-lg hover:bg-gray-100 transition-colors duration-300">
                                        get started
                                    </button>
                                    </a>
                                </div>
                            </article>
                        </ScrollReveal>
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
};

export default SafariDetail;
